import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { RouterLink, Router, ActivatedRoute, Params } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../../services/api.service';
import { CreativeAnalysisService } from '../../../services/creative-analysis.service';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CreativeAnalysisImage, CreativeAnalysisVideo, CreativeAnalysisCarousel, CreativeFormat, CarouselCreative } from '../../../interfaces/creative-analysis';
import { environment } from '../../../../environments/environment';
import { NgxJsonViewerModule } from 'ngx-json-viewer';

import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-creative-analysis-single',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    NgbTooltipModule,
    NgxJsonViewerModule
  ],
  templateUrl: './creative-analysis-single.component.html',
  styleUrl: './creative-analysis-single.component.css'
})
export class CreativeAnalysisSingleComponent implements OnInit {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  creativeAnalysisService: CreativeAnalysisService = inject(CreativeAnalysisService);
  apiService: ApiService = inject(ApiService);
  toastrService: ToastrService = inject(ToastrService);

  // User Authentication
  authenticatedUser = this.authService.authenticatedUser;

  // Component-specific variables
  accountId: string = '';
  showDebugData: boolean = false;

  environment = environment;

  creative: string | null = null;
  fileToUpload: File | null = null;
  s3UploadUrl: string | null = null;
  fileOriginalName: string = '';
  fileTempName: string = '';
  fileType: string = '';
  thumbnail: string | null = null;
  thumbnailUpload = `${environment.publicAssetsBucketUrl}/app-public/images/cloud-upload.gif`;
  url: string | null = null;
  creativeType: 'IMG' | 'VID' | 'CAR' | null = null;
  // jobID: string = '';

  loadingCreative = false;
  uploadingCreative = false;
  requestingCreativeAnalysis = false;
  loadingCreativeAnalysis = false;

  creativeAnalysisImage: CreativeAnalysisImage | null = null;
  creativeAnalysisVideo: CreativeAnalysisVideo | null = null;
  creativeAnalysisCarousel: CreativeAnalysisCarousel | null = null;
  creativeAnalysis: CreativeAnalysisImage | CreativeAnalysisVideo | CreativeAnalysisCarousel | null = null;
  creativeStyles: string[] = [];
  alternativeCreativeStyles: string[] = [];
  selectCreativeStyles: string[] = [];
  creativeFormats: CreativeFormat[] = [
    {id: 'high-fidelity', name: 'High Fidelity', forImage: true, forVideo: true},
    {id: 'low-fidelity', name: 'Low Fidelity', forImage: true, forVideo: true},
    {id: 'motion-graphics-animated', name: 'Motion Graphics/Animated', forImage: false, forVideo: true},
    {id: 'gif', name: 'GIF', forImage: false, forVideo: true},
    {id: 'na', name: 'N/A', forImage: true, forVideo: true}
  ];
  selectCreativeFormat: string[] = [];
  selectPersonFeature: string[] = ['Yes', 'No'];
  selectUgc: string[] = ['Yes', 'No'];
  selectMarketAwareness: string[] = ['Unaware', 'Problem Aware' , 'Solution Aware', 'Product Aware', 'Most Aware'];
  selectGender: string[] = ['Female', 'Male', 'Unisex', 'N/A'];
  selectNeedsExpertReview: boolean = false;
  selectNeedsQa: boolean = false;
  updateField: {
    [key: string]: {
      name: string;
      showButton: boolean;
      showLoading: boolean;
      updateStatus: 'saved' | 'error' | null;
    };
  } = {
    creative_style: {
      name: 'creative_style',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    creative_format: {
      name: 'creative_format',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    main_angle: {
      name: 'main_angle',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    target_market: {
      name: 'target_market',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    market_awareness: {
      name: 'market_awareness',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    usps: {
      name: 'usps',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    discounts: {
      name: 'discounts',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    ugc: {
      name: 'ugc',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    headline: {
      name: 'headline',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    person_feature: {
      name: 'person_feature',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    call_to_action: {
      name: 'call_to_action',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    audio_hook: {
      name: 'audio_hook',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    visual_hook: {
      name: 'visual_hook',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    text_hook: {
      name: 'text_hook',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    creator: {
      name: 'creator',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    age_group: {
      name: 'age_group',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    gender: {
      name: 'gender',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    length: {
      name: 'length',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    audio_close: {
      name: 'audio_close',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    visual_close: {
      name: 'visual_close',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    text_close: {
      name: 'text_close',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    number_of_tiles: {
      name: 'number_of_tiles',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    needs_expert_review: {
      name: 'needs_expert_review',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    needs_expert_review_reason: {
      name: 'needs_expert_review_reason',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
    needs_qa: {
      name: 'needs_qa',
      showButton: true,
      showLoading: false,
      updateStatus: null
    },
  };

  carouselCreatives: CarouselCreative[] = [];
  
  selectedModel: 'openai' | 'gemini' = 'gemini';
  dropdownModelOpen = false;

  private pollingSubscription: Subscription = new Subscription();

  ngOnInit(): void {
    
    // Check that user is authenticated (should be the case if this path is protected with AuthGuardService in app.routes.ts)
    this.authService.checkUserSignedIn().then(checkSign => {
      if (!checkSign) {
        // User is NOT signed in => Call AuthService.signOut() to redirect to Sign in page
        this.authService.signOut();
      } else {
        // User is signed in
                      
        // Only show Debug information to Matt, Robyn & Ferree for now
        if(this.authenticatedUser && ['matt.f@webprofits.com.au', 'robyn.e@webprofits.com.au', 'ferree.h@webprofits.com.au'].includes(this.authenticatedUser.email)) {
          this.showDebugData = true;
        }

        // Get Ad Account ID from URL params
        this.activatedRoute.params.subscribe((params: Params) => {
          this.accountId = params['id'];
          this.creative = params['creative'].toUpperCase();
          if(this.creative && (this.creative.length < 6 || this.creative.length > 6)) {
            console.error('Invalid Creative ID');
            this.toastrService.error('Invalid Creative ID');
            this.router.navigate(['/']);
          }
          if(this.creative) {
            console.log(`Loading Creative ID: ${this.creative}`);
            this.getCreative(this.accountId, this.creative);
          }
          // Get the Creative Type from the Creative ID
          if(this.creative) {
            this.creativeType = this.creative.substring(0, 3) as 'IMG' | 'VID' | 'CAR';
            if(this.creativeType === 'VID') {
              console.log('Video creative detected. Setting model to:', this.selectedModel);
            }
          }
          console.log(`Loading Ad Account#: ${this.accountId}`);
          // Check if the user has access to the Ad Accounts section & this specific ad account
          this.authService.checkUserHasAccessToAdAccounts().then(adAccounts => {
            if(adAccounts === null || !adAccounts.some(adAccount => adAccount.id === this.accountId)) {
              // User does not have access to the account (or the user has no ad accounts at all)
              console.log("User does not have access to this ad account");
              this.toastrService.error("You do not have access to this Ad Account", "Access Denied");
              this.router.navigate(['/']);
            } else {
              // User has access to the account
              console.log("User has access to this ad account");

            }        
          });
          // Load creative styles
          this.getCreativeStyles();
        });
      }  
    });
  }

  // Add this new method for trackBy
  trackByIndex(index: number, item: any): number {
    return index; // Use the index as the unique identifier
  }

  fileChangeEvent(event: any): void {
    this.fileToUpload = event.target.files[0];
    if(this.fileToUpload) {
      console.log('File selected:', this.fileToUpload.name);
      // Regex to match "IMG", "VID" or "CAR" followed by exactly three digits
      const pattern = /^(IMG|VID|CAR)(\d{3})/;
      const matches = this.fileToUpload.name.match(pattern);
      console.log('Matches:', matches);
      if(this.creative) {
        // Creative ID is provided
        // Check if the file name matches the creative
        if(matches && matches[0] === this.creative) {
          console.log('File name matches the creative ID');
        } else {
          console.error('File name does not match the creative ID');
          this.toastrService.warning('File name does not match the creative ID', 'Warning!!', { disableTimeOut: true });
        }
      } else {
        // Creative ID is not provided
        // Set the creative ID to the first match
        if(matches) {
          console.log('Creative ID set to:', this.creative);
        } else {
          console.error('Creative ID not found in the file name');
          this.toastrService.warning('Creative ID not found in the file name', 'Warning!!', { disableTimeOut: true });
        }
      }
    }
  }

  uploadCreative(event: Event): void {
    event.preventDefault();
    if (!this.fileToUpload) {
      console.error('No file selected for upload.');
      this.toastrService.error('No file selected for upload.');
      return;
    }

    this.fileOriginalName = this.fileToUpload.name;
    this.fileType = this.fileToUpload.type;
    console.log('Uploading file:', this.fileOriginalName, 'of type:', this.fileType);

    // Get S3 temporary user storage Upload URL
    this.s3UploadUrl = null;
    this.uploadingCreative = true;
    this.creativeAnalysisService.getS3UploadUrl(this.fileOriginalName, this.fileType).then(response => {
      console.log('S3 Upload URL', response);
      this.s3UploadUrl = response.url;
      this.fileTempName = response.t_name;
      // Upload file to S3 temporary user storage
      return this.creativeAnalysisService.uploadFileToS3(this.s3UploadUrl!, this.fileToUpload!);
    }).then(() => {
      console.log('File uploaded to S3');
      // Import the creative from S3 temporary user storage to Firestore & S3 permanent creative analysis storage
      return this.creativeAnalysisService.importCreative(this.accountId, this.creative!, this.fileOriginalName, this.fileTempName);
    }).then((importedCreative) => {
      console.log('Creative imported successfully', importedCreative);
      this.thumbnail = importedCreative.thumbnail ?? `${environment.publicAssetsBucketUrl}/app-public/images/cloud-upload.gif`;
      this.toastrService.success('Creative imported successfully');
      // Request analysis
      return this.creativeAnalysisService.requestAnalysis(this.accountId, this.creative!, this.selectedModel);
    }).then((requestAnalysisResponse) => {
      console.log('Analysis requested', requestAnalysisResponse);
      this.uploadingCreative = false;

      // Reset form
      this.fileToUpload = null;
      this.s3UploadUrl = null;
      this.fileOriginalName = '';
      this.fileTempName = '';
      this.fileType = '';


      console.log('Starting polling for job status...');
      this.startPolling(this.accountId, this.creative!);


    }).catch(error => {
      console.error('Error getting S3 Upload URL', error);
      this.uploadingCreative = false;
    });

  }

  startPolling(accountId: string, creativeId: string): void {
    const pollingInterval = 2000; // 2 seconds

    this.requestingCreativeAnalysis = true;

    this.pollingSubscription = interval(pollingInterval).pipe(
      switchMap(() => this.creativeAnalysisService.checkJobStatus(accountId, creativeId)),
      takeWhile(response => response.status !== 'completed' && response.status !== 'failed' && response.status !== 'error', true)
    ).subscribe(response => {
      console.log('Polling response:', response);
      if(response.thumbnail) {
        this.thumbnail = response.thumbnail;
      }
      if(response.url) {
        this.url = response.url;
      }
      if(response.status === 'completed' || response.status === 'failed' || response.status === 'error') {        
        this.pollingSubscription.unsubscribe(); // Stop polling
        this.requestingCreativeAnalysis = false;
        if(response.status === 'completed') {
          console.log('Creative processing completed successfully');
          this.loadCreativeAnalysis(accountId, creativeId);
          
        }
        if(response.status === 'failed' || response.status === 'error') {
          console.error('Creative processing failed', response);
          this.toastrService.error(`Creative processing failed [${response.error}]`);
        }
      }
    }, error => {
      console.error('Polling error:', error);
      this.toastrService.error('Error during status check.');
      this.pollingSubscription.unsubscribe(); // Stop polling on error
    });
  }

  loadCreativeAnalysis(accountId: string, creativeId: string): void {
    this.creativeAnalysisImage = null;
    this.creativeAnalysisVideo = null;
    this.creativeAnalysisCarousel = null;
    this.creativeAnalysis = null;
    this.loadingCreativeAnalysis = true;

    // Reset updateField array
    for (const key in this.updateField) {
      if (this.updateField.hasOwnProperty(key)) {
        this.updateField[key].updateStatus = null;
        this.updateField[key].showButton = true;
        this.updateField[key].showLoading = false;
      }
    }
    // Get type of creative from the creative ID
    const creativeType = creativeId.substring(0, 3) as 'IMG' | 'VID' | 'CAR';
    console.log('Creative type:', creativeType);
    if(creativeType === 'IMG') {
      this.creativeAnalysisService.getCreativeAnalysisImage(accountId, creativeId).then(analysis => {
        console.log('Creative analysis:', analysis);
        this.loadingCreativeAnalysis = false;
        this.creativeAnalysisImage = analysis;
        this.creativeAnalysis = analysis;

        // Ensure usps is an array and pad it
        if (!Array.isArray(this.creativeAnalysis.usps)) {
          this.creativeAnalysis.usps = [];
        }
        while (this.creativeAnalysis.usps.length < 10) {
          this.creativeAnalysis.usps.push('');
        }

        this.alternativeCreativeStyles = [];
        if(analysis.creativeStyleAlternatives) {
          analysis.creativeStyleAlternatives.forEach((style: string) => {
            this.alternativeCreativeStyles.push(style);
          });
          console.log("Alternative Creative Styles", this.alternativeCreativeStyles);
        }
        this.buildSelectCreativeStyles(analysis.creativeStyle!);
        this.buildSelectCreativeFormat();
  
      }).catch(error => {
        console.error('Error fetching creative analysis', error);
        this.loadingCreativeAnalysis = false;
        this.toastrService.error('Error fetching creative analysis');
      });
    } else if(creativeType === 'VID') {
      this.creativeAnalysisService.getCreativeAnalysisVideo(accountId, creativeId).then(analysis => {
        console.log('Creative analysis:', analysis);
        this.loadingCreativeAnalysis = false;
        this.creativeAnalysisVideo = analysis;
        this.creativeAnalysis = analysis;

        // Ensure usps is an array and pad it
        if (!Array.isArray(this.creativeAnalysis.usps)) {
          this.creativeAnalysis.usps = [];
        }
        while (this.creativeAnalysis.usps.length < 10) {
          this.creativeAnalysis.usps.push('');
        }

        this.alternativeCreativeStyles = [];
        if(analysis.creativeStyleAlternatives) {
          analysis.creativeStyleAlternatives.forEach((style: string) => {
            this.alternativeCreativeStyles.push(style);
          });
          console.log("Alternative Creative Styles", this.alternativeCreativeStyles);
        }
        this.buildSelectCreativeStyles(analysis.creativeStyle!);
        this.buildSelectCreativeFormat();
  
      }).catch(error => {
        console.error('Error fetching creative analysis', error);
        this.loadingCreativeAnalysis = false;
        this.toastrService.error('Error fetching creative analysis');
      });
    } else if(creativeType === 'CAR') {
      this.creativeAnalysisService.getCreativeAnalysisCarousel(accountId, creativeId).then(analysis => {
        console.log('Creative analysis:', analysis);
        this.loadingCreativeAnalysis = false;
        this.creativeAnalysisCarousel = analysis;
        this.creativeAnalysis = analysis;

        // Ensure usps is an array and pad it
        if (!Array.isArray(this.creativeAnalysis.usps)) {
          this.creativeAnalysis.usps = [];
        }
        while (this.creativeAnalysis.usps.length < 10) {
          this.creativeAnalysis.usps.push('');
        }
                
        this.alternativeCreativeStyles = [];
        if(analysis.creativeStyleAlternatives) {
          analysis.creativeStyleAlternatives.forEach((style: string) => {
            this.alternativeCreativeStyles.push(style);
          });
          console.log("Alternative Creative Styles", this.alternativeCreativeStyles);
        }
        this.buildSelectCreativeStyles(analysis.creativeStyle!);
        this.buildSelectCreativeFormat();
  
      }).catch(error => {
        console.error('Error fetching creative analysis', error);
        this.loadingCreativeAnalysis = false;
        this.toastrService.error('Error fetching creative analysis');
      });
    }
    
  }

  requestAnalysis(): void {
    this.requestingCreativeAnalysis = true;
    this.creativeAnalysisService.requestAnalysis(this.accountId, this.creative!, this.selectedModel).then(response => {
      console.log('Creative analysis requested', response);
      this.requestingCreativeAnalysis = false;
      // this.jobID = response.job;
      this.startPolling(this.accountId, this.creative!);
    }).catch(error => {
      console.error('Error requesting creative analysis', error);
      this.requestingCreativeAnalysis = false;
      this.toastrService.error('Error requesting creative analysis');
    });
  }

  getCreative(accountId: string, creativeId: string): void {
    try {
      this.loadingCreative = true;
      this.creativeAnalysisService.getCreative(accountId, creativeId)
      .then(creative => {
        console.log('Creative:', creative);
        this.loadingCreative = false;
        this.thumbnail = creative.thumbnail;
        this.url = creative.url;
        this.loadCreativeAnalysis(accountId, creativeId);

        // If it's a carousel, load previews of its creatives
        this.creativeAnalysisService.getCarouselCreatives(accountId, creativeId)
        .then(carouselCreatives => {
          console.log("[getCarouselCreatives] Carousel creatives", carouselCreatives);
          let creatives: CarouselCreative[] = [];
          if(carouselCreatives && carouselCreatives.length > 0) {
            _.forEach(carouselCreatives, (creative => {
              creatives.push({
                id: creative.id,
                thumbnail: creative.thumbnail,
                url: creative.url,
                fileName: creative.fileName
              })
            }))
          }
          this.carouselCreatives = creatives;
          console.log("this.carouselCreatives", this.carouselCreatives);
        })
        .catch(error => {
          console.error('[getCarouselCreatives] Error fetching carousel creatives', error);
        });
      })
      .catch(error => {
        console.error('Error fetching creative', error);
        this.loadingCreative = false;
        if(error == 'CreativeNotFound') {
          this.toastrService.warning('Creative not found. Upload a file to get started.');
          this.thumbnail = `${environment.publicAssetsBucketUrl}/app-public/images/cloud-upload.gif`
        } else {
          this.toastrService.error(`Error fetching creative [${error}]`);
        }
      });
    } catch (error) {
      console.error('Error fetching creative', error);
      this.loadingCreative = false;
    }
  }

  updateCreative(field: 'creative_style' | 'creative_format' | 'main_angle' | 'target_market' | 'market_awareness' | 'usps' | 'discounts' | 'ugc' | 'headline' | 'person_feature' | 'call_to_action' | 'audio_hook' | 'visual_hook' | 'text_hook' | 'creator' | 'age_group' | 'gender' | 'length' | 'audio_close' | 'visual_close' | 'text_close' | 'number_of_tiles' | 'needs_expert_review' | 'needs_expert_review_reason' | 'needs_qa'): void {
    let updateValue: any;

    // ❗ Fallback to DOM value for other inputs/selects not using model binding
    const element = document.getElementById(`update-${field}`) as HTMLInputElement | HTMLSelectElement;
    updateValue = element?.value ?? undefined;
  
    if (field === 'needs_expert_review') {
      // ✅ For checkbox, use model binding
      updateValue = this.creativeAnalysis?.needsExpertReview;
    }
  
    if (field === 'needs_qa') {
      // ✅ For checkbox, use model binding
      updateValue = this.creativeAnalysis?.needsQa;
    }

    if (field === 'usps') {
      // Clear any null or empty space values
      updateValue = (this.creativeAnalysis?.usps ?? []).filter(
        usp => usp && usp.trim().length > 0
      );
    }
  
    console.log('Updating creative field:', field, 'with value:', updateValue);
  
    if (field === 'creative_style') {
      if (!this.creativeStyles.includes(updateValue)) {
        console.error('Selected style is not in the list of creative styles');
        this.toastrService.error('Selected style must be in the list of Creative Styles');
        return;
      }
    }

    if (field === 'creative_format') {
      let checkFormat = false;
      this.creativeFormats.forEach(format => {
        if(this.creativeType === 'IMG' && format.forImage && format.name === updateValue) checkFormat = true;
        if((this.creativeType === 'VID' || this.creativeType === 'CAR') && format.forVideo && format.name === updateValue) checkFormat = true;
      })
      if(!checkFormat) {
        console.error('Selected format is not in the list of creative format');
        this.toastrService.error('Selected format must be in the list of Creative Formats');
        return;
      }
    }
  
    if (field === 'market_awareness') {
      if (!this.selectMarketAwareness.includes(updateValue)) {
        console.error('Selected value is not in the list of market awareness');
        this.toastrService.error('Selected value must be in the list of Market Awareness');
        return;
      }
    }
  
    if (field === 'person_feature') {
      if (!this.selectPersonFeature.includes(updateValue)) {
        console.error('Selected value is not in the list of person features');
        this.toastrService.error('Selected value must be in the list of Person Features');
        return;
      }
    }

    if (field === 'ugc') {
      if (!this.selectUgc.includes(updateValue)) {
        console.error('Selected value is not in the list of UGC');
        this.toastrService.error('Selected value must be in the list of UGC');
        return;
      }
    }
  
    if (updateValue !== undefined) {
      const data: Record<string, any> = {};
      data[field] = updateValue;
      console.log('Data to send:', data);
  
      this.updateField[field].showButton = false;
      this.updateField[field].showLoading = true;
  
      this.creativeAnalysisService.updateCreative(this.accountId, this.creative!, data)
        .then(() => {
          console.log('Creative updated successfully');
          this.updateField[field].showButton = true;
          this.updateField[field].showLoading = false;
          this.updateField[field].updateStatus = 'saved';
          this.toastrService.success('Creative updated successfully');
        })
        .catch(error => {
          console.error('Error updating creative', error);
          this.updateField[field].showButton = true;
          this.updateField[field].showLoading = false;
          this.updateField[field].updateStatus = 'error';
          this.toastrService.error('Error updating creative');
        });
    } else {
      // Undefined update value
      console.error('Invalid update data');
      this.toastrService.error('The update value is invalid');
      return;
    }
  }

  toggleDropdownModel() {
    this.dropdownModelOpen = !this.dropdownModelOpen;
  }

  closeDropdownModel(model: 'openai' | 'gemini') {
    this.dropdownModelOpen = false;
    this.selectedModel = model;
    console.log('Refreshing AI classification using:', this.selectedModel);
    this.requestAnalysis();
  }

  getCreativeStyles() {
    console.log('Loading creative styles...');
    this.creativeAnalysisService.listCreativeStyles(this.accountId).then(creativeStyles => {
      let styles: string[] = [];
      creativeStyles.forEach((style: any) => {
        if(style.name) {
          if(this.creativeType === 'VID') {
            if(style.forVideo) {
              styles.push(style.name);
            }
          } else if(this.creativeType === 'IMG') {
            if(style.forImage) {
              styles.push(style.name);
            }
          } else if(this.creativeType === 'CAR') {
            styles.push(style.name);
          } else {
            styles.push(style.name);
          }
        }
      });
      this.creativeStyles = styles;
      console.log("Loaded Creative Styles", this.creativeStyles);
    }).catch(error => {
      console.error('Error fetching creative styles', error);
      this.toastrService.error('Error fetching creative styles');
    });
  }

  buildSelectCreativeStyles(selectedStyle: string): void {
    if (!this.alternativeCreativeStyles.includes(selectedStyle)) {
      this.alternativeCreativeStyles.unshift(selectedStyle); // Add the selected style at the start
    }

    // Remove the top styles from the main array
    const remainingStyles = this.creativeStyles.filter(style => !this.alternativeCreativeStyles.includes(style));

    // Sort the remaining styles alphabetically
    remainingStyles.sort();

    // Combine all parts together
    this.selectCreativeStyles = [...this.alternativeCreativeStyles, '---', ...remainingStyles]; // Adding a separator
  }

  buildSelectCreativeFormat(): void {
    this.creativeFormats.forEach(format => {
      if(this.creativeType === 'IMG' && format.forImage) this.selectCreativeFormat.push(format.name);
      if((this.creativeType === 'VID' || this.creativeType === 'CAR') && format.forVideo) this.selectCreativeFormat.push(format.name);
    })
  }


}
